<template>
  <AnonLayout>
    <AnonFormWrapper title="Login">
      <form @submit.prevent="handleSubmit">
        <div class="mb-4">
          <label class="mb-2 inline-block" for="email">Email</label>
          <VInput id="email" type="email" v-model="email" autocomplete="username webauthn" autofocus />
        </div>
        <div class="mb-4">
          <label class="mb-2 inline-block" for="password">Password</label>
          <VInput id="password" type="password" v-model="password" autocomplete="current-password" />
          <span class="mt-1 block text-right">
            <router-link
              to="/forgot"
              class="text-[13px] text-gpx_terracotta-200 hover:text-gpx_red-200 hover:underline"
            >
              Forgot your password?
            </router-link>
          </span>
        </div>
        <Button
          type="submit"
          :loading="isSubmitting"
          loadingText="Logging in..."
          class="mb-2 w-full"
        >
          Login
        </Button>
      </form>
      <div class="relative mt-6">
        <div class="absolute inset-0 flex items-center">
          <div class="w-full border-t border-gpx_gray-800"></div>
        </div>
        <div class="relative flex justify-center text-sm">
          <span class="bg-white px-2 text-gpx_gray-700">Or</span>
        </div>
      </div>
      <div class="mt-6 flex justify-center">
        <div ref="googleButtonRef"></div>
      </div>
      <p v-if="googleError" class="mt-2 text-center text-sm text-gpx_red-200">
        {{ googleError }}
      </p>
    </AnonFormWrapper>
  </AnonLayout>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';
import NProgress from 'nprogress';
import { useGoogleSignIn, usePasskeyAuth } from '@gpx-spa/ui';
import AnonLayout from '@/layouts/AnonLayout.vue';
import AnonFormWrapper from '@/components/_common/AnonFormWrapper.vue';
import VInput from '@/components/ui/VInput.vue';
import { Button } from '@gpx-spa/ui';
import { VITE_APP_GOOGLE_CLIENT_ID } from '@/config/environment';
import service from '@/services';

const store = useStore();
const router = useRouter();
const route = useRoute();
const toast = useToast();

const email = ref('');
const password = ref('');
const isSubmitting = ref(false);

const isLoggedIn = computed(() => store.getters['auth/check']);

const {
  authenticateConditional,
  isConditionalMediationAvailable,
} = usePasskeyAuth();

let conditionalAbort = null;

async function startConditionalPasskeyLogin() {
  try {
    const available = await isConditionalMediationAvailable();
    if (!available) return;
    const optionsRes = await service.post('/passkeys/authenticate/options');
    const assertion = await authenticateConditional(optionsRes.data, conditionalAbort?.signal);
    if (!assertion) return;
    const payload = { ...assertion, sessionId: optionsRes.data.sessionId };
    await store.dispatch('auth/passkeyLogin', {
      assertion: payload,
      next: route.query.next,
    });
  } catch (err) {
    if (err?.name !== 'AbortError' && err?.name !== 'NotAllowedError') {
      console.debug('[Passkey] Conditional login failed:', err);
    }
  }
}

onUnmounted(() => {
  conditionalAbort?.abort();
});

const googleButtonRef = ref(null);
const { error: googleError, init: initGoogleSignIn } = useGoogleSignIn({
  clientId: VITE_APP_GOOGLE_CLIENT_ID,
  buttonRef: googleButtonRef,
  onError: (message) => toast.error(message),
});

function handleSubmit() {
  conditionalAbort?.abort();
  NProgress.start();
  isSubmitting.value = true;

  store
    .dispatch('auth/login', {
      credentials: {
        email: email.value,
        password: password.value,
      },
      next: route.query.next,
    })
    .catch((e) => {
      toast.error(e.response?.data?.message || 'An error occurred.');
      NProgress.done();
      isSubmitting.value = false;
    });
}

watch(isLoggedIn, (val) => {
  if (val) {
    router.push({ name: 'DeviceListView' });
  }
});

onMounted(() => {
  if (isLoggedIn.value) {
    router.push({ name: 'DeviceListView' });
    return;
  }
  initGoogleSignIn((idToken) =>
    store.dispatch('auth/googleLogin', {
      id_token: idToken,
      next: route.query.next,
    }),
  );
  conditionalAbort = new AbortController();
  startConditionalPasskeyLogin();
});
</script>
